import { toInterviewCardStatus } from '~/components/CandidateOverview/candidateDetailsView';
import {
  IGetOnsiteInterviewListViewEntity,
  OnsiteInterviewStatusMapper,
} from '~/domain/PermanentJobs';
import { JobStatusEnum } from '~/models';
import { SHIFT_CARD_STATUS, type TShiftCardData } from '~/types/ShiftCard';
import { computeHours, formatRate, parseCardDate } from '~/utils/shiftCard';

interface IPostingCardContext {
  /** The posting's office — every interview of a posting is at that office. */
  officeName: string;
  /** Profession badge code, e.g. "RDH" / "EFDA": the posting's sub-profession, else its profession. */
  profession?: string;
}

/**
 * Translates one of a posting's onsite interviews into props for the DS
 * `DentalOfficeShiftCard`, so the Interviews tab draws the same card as every other
 * shift list. The card status comes from the interview's status bucket, the one the
 * filter uses, so a card always sits under the filter it reads as. The status rule
 * is Candidate Details' `toInterviewCardStatus`, so both pages colour an interview
 * the same way.
 */
export const mapOnsiteInterviewToShiftCard = (
  interview: IGetOnsiteInterviewListViewEntity,
  { officeName, profession }: IPostingCardContext,
): TShiftCardData => {
  const status = toInterviewCardStatus(OnsiteInterviewStatusMapper.fromJobStatus(interview.status));

  const data: TShiftCardData = {
    status,
    date: parseCardDate(interview.localDate),
    startTime: interview.localTimeStartsAt,
    endTime: interview.localTimeEndsAt,
    hours: computeHours(interview.localTimeStartsAt, interview.localTimeEndsAt),
    rate: formatRate(interview.rate),
    profession,
    office: officeName,
    interview: true,
  };

  // The card collapses expired into `cancelled`; keep the two apart in the status row.
  if (interview.status === JobStatusEnum.Expired) {
    data.statusLabel = 'Expired';
  }

  // Only a booked interview shows its professional, and only when there is a name or
  // a photo to show: an empty `person` would still draw a nameless avatar row.
  const { professional } = interview;
  if (
    (professional?.fullName || professional?.imageUrl) &&
    (status === SHIFT_CARD_STATUS.SCHEDULED || status === SHIFT_CARD_STATUS.COMPLETED)
  ) {
    data.person = {
      ...(professional.fullName ? { name: professional.fullName } : {}),
      ...(professional.imageUrl ? { avatarUrl: professional.imageUrl } : {}),
    };
  }

  return data;
};
